import "@xyflow/react/dist/style.css";

import { Box, Paper, Stack, Typography } from "@mui/material";
import { FormProvider, useForm } from "react-hook-form";
import { ReactFlow, useEdgesState, useNodesState } from "@xyflow/react";
import {
    buildInitialFormValues,
    getJobNameToModuleVersionMap,
} from "./utils/form";
import { getEdges, getNodes, nodeTypes } from "./utils/flowLayout";

import { AbortButton } from "./components/AbortButton";
import { MODEL_PROPTYPES } from "../../models";
import { PIPELINE_INSTANCE_STATUS } from "../../constants";
import { PresetSelect } from "./components/PresetSelect";
import PropTypes from "prop-types";
import { StatusChip } from "./components/StatusChip";
import { SubmitButton } from "./components/SubmitButton";
import { formatDateTime } from "../../utils/formatTime";
import { getRelaunchableJobNames } from "./utils/submit";

PipelineInstance.propTypes = {
    pipelineInstance: PropTypes.shape(MODEL_PROPTYPES.PipelineInstance)
        .isRequired,
    moduleVersions: PropTypes.arrayOf(
        PropTypes.shape(MODEL_PROPTYPES.ModuleVersion)
    ).isRequired,
    presets: PropTypes.arrayOf(
        PropTypes.shape(MODEL_PROPTYPES.CropModulePreset)
    ),
    experimentCropUuid: PropTypes.string,
    mutate: PropTypes.func.isRequired,
};

export function PipelineInstance({
    pipelineInstance,
    moduleVersions,
    presets,
    experimentCropUuid,
    mutate,
}) {
    const isPlanned =
        pipelineInstance.status === PIPELINE_INSTANCE_STATUS.PLANNED;

    // Only apply the default preset to planned instances: once launched,
    // the job args already hold the values the instance was launched with.
    const defaultPreset = isPlanned
        ? presets?.find((ps) => ps.default)
        : undefined;

    const jobs = pipelineInstance.translatedPipeline?.spec?.jobs || [];

    const initialFormValues = buildInitialFormValues(
        jobs,
        moduleVersions,
        defaultPreset
    );
    const jobNameToModuleVersion = getJobNameToModuleVersionMap(
        jobs,
        moduleVersions
    );

    const isRelaunchable =
        pipelineInstance.status === PIPELINE_INSTANCE_STATUS.ERROR ||
        pipelineInstance.status === PIPELINE_INSTANCE_STATUS.FINISHED;

    // Which job nodes' argument fields are editable:
    // - Planned (Operate flow): every job.
    // - Error/Finished (relaunch flow): only the photogrammetry job and
    //   its downstream dependents (what a relaunch actually restarts).
    // - Otherwise: none.
    const editableJobNames = isPlanned
        ? new Set(jobs.map((job) => job.name))
        : isRelaunchable
          ? getRelaunchableJobNames(jobs)
          : new Set();

    const formMethods = useForm({
        defaultValues: initialFormValues,
    });

    const missionUuid =
        pipelineInstance.translateParams?.variables?.missionUuid;
    const experimentUuids =
        pipelineInstance.translateParams?.variables?.experimentUuid;

    const initialNodes = getNodes(
        jobs,
        moduleVersions,
        editableJobNames,
        missionUuid,
        experimentUuids,
        experimentCropUuid
    );
    const initialEdges = getEdges(jobs);
    const [nodes, , onNodesChange] = useNodesState(initialNodes);
    const [edges, , onEdgesChange] = useEdgesState(initialEdges);

    return (
        <FormProvider {...formMethods}>
            <Stack sx={{ mb: 1 }} direction="row" spacing={1} alignItems="end">
                <PipelineInstanceTitle
                    status={pipelineInstance.status}
                    createdAt={pipelineInstance.createdAt}
                />
                <PresetSelect
                    presets={presets}
                    initialPreset={defaultPreset}
                    jobNameToModuleVersion={jobNameToModuleVersion}
                    disabled={!isPlanned}
                />
                <Box sx={{ flexGrow: 1 }} />
                <AbortButton
                    pipelineInstance={pipelineInstance}
                    mutate={mutate}
                />
                <SubmitButton
                    pipelineInstance={pipelineInstance}
                    mutate={mutate}
                    jobNameToModuleVersion={jobNameToModuleVersion}
                />
            </Stack>
            <Paper
                sx={{
                    height: "75dvh",
                    width: "100%",
                    overflow: "hidden",
                    backgroundColor: "#f5f5f5",
                    borderRadius: 2,
                    border: "1px solid #ddd",
                    boxShadow: "inset 0 0 10px rgba(0,0,0,0.1)",
                    position: "relative",
                }}
            >
                <ReactFlow
                    nodeTypes={nodeTypes}
                    nodes={nodes}
                    edges={edges}
                    onNodesChange={onNodesChange}
                    onEdgesChange={onEdgesChange}
                    deleteKeyCode={null}
                    fitView
                    proOptions={{ hideAttribution: true }}
                />
            </Paper>
        </FormProvider>
    );
}

function PipelineInstanceTitle({ status, createdAt }) {
    return (
        <Stack direction="column">
            <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="h4">Pipeline Instance</Typography>
                <StatusChip status={status} />
            </Stack>
            <Typography variant="body2" color="text.secondary">
                Created: {formatDateTime(createdAt)}
            </Typography>
        </Stack>
    );
}
